import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Combobox, type ComboboxOption } from '@evogard/ui';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * Os dois seletores da abertura de demanda na interna: a placa (`demandas-44`)
 * e o solicitante (`demandas-45`). Os dois são o mesmo gesto — "procure e
 * escolha um" —, e por isso o mesmo `Combobox`, com fontes diferentes.
 *
 * **Quem decide o que cada um mostra é o servidor.** O consultor recebe só a
 * carteira dele na busca de placa, sem a tela mandar nada que diga isso; e o
 * solicitante nem aparece para ele (quem esconde é `OpeningForm`, pelo
 * `openingProfile`, e quem recusa é a gravação).
 */

/**
 * O começo de uma placa sai em maiúscula e sem hífen enquanto se digita: quem
 * escreve "abc-1d" vê "ABC1D", que é o que a busca casa. Um nome ("Fulana de
 * Tal") passa como veio — tirar o espaço dele juntaria as palavras.
 */
export function plateAsTyped(term: string): string {
  return /^[a-z]{3}-?\d/i.test(term.trim())
    ? term.toUpperCase().replace(/[^A-Z0-9]/g, '')
    : term;
}

const TERM_MIN = 2;

export function VehiclePicker({
  value,
  onChange,
  consultant,
}: {
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  /** só muda o texto de ajuda: o recorte da carteira é do servidor */
  consultant: boolean;
}) {
  const [term, setTerm] = useState('');
  /* a base na chave: a carteira e o cadastro são da praça, e trocar de base
     não pode servir as placas da outra do cache */
  const tenantId = useSessionContext().data?.currentTenant.id;
  const debounced = useDebouncedTerm(term, 300);
  const ready = debounced.length >= TERM_MIN;

  const search = useQuery({
    queryKey: ['demandas', 'abertura', 'placas', tenantId, debounced],
    enabled: ready,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await api.tickets.searchVehicles({ body: { term: debounced } });
      return ok(r, 200, 'Não foi possível buscar as placas.');
    },
  });

  const options: ComboboxOption[] =
    search.data?.estado === 'lida'
      ? search.data.items.map((v) => ({
          value: v.code,
          /* sem placa, o código: o veículo existe e é escolhível mesmo assim */
          label: v.placa ?? `Veículo ${v.code}, sem placa`,
          detail: [
            [v.marca, v.modelo, v.anoModelo].filter(Boolean).join(' '),
            v.owner,
            v.situacao,
          ]
            .filter(Boolean)
            .join(' · '),
        }))
      : [];

  return (
    <Combobox
      label="Placa (opcional)"
      value={value}
      onChange={onChange}
      onSearch={setTerm}
      options={options}
      loading={ready && (search.isFetching || term !== debounced)}
      normalize={plateAsTyped}
      minLength={TERM_MIN}
      placeholder="Nenhuma placa"
      searchPlaceholder="Começo da placa ou nome do associado"
      emptyMessage={
        consultant
          ? 'Nenhuma placa da sua carteira com esse começo.'
          : 'Nenhuma placa encontrada.'
      }
      notice={search.isError ? search.error.message : undefined}
      help={
        consultant
          ? 'Só aparecem os veículos da sua carteira.'
          : 'Se o ticket for sobre um veículo, escolha a placa.'
      }
    />
  );
}

export function RequesterPicker({
  value,
  onChange,
}: {
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
}) {
  const [term, setTerm] = useState('');
  /* a base na chave: a carteira e o cadastro são da praça, e trocar de base
     não pode servir as placas da outra do cache */
  const tenantId = useSessionContext().data?.currentTenant.id;
  const debounced = useDebouncedTerm(term, 300);
  const ready = debounced.length >= TERM_MIN;

  const search = useQuery({
    queryKey: ['demandas', 'abertura', 'solicitantes', tenantId, debounced],
    enabled: ready,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await api.tickets.searchRequesters({ query: { term: debounced } });
      return ok(r, 200, 'Não foi possível buscar as pessoas.');
    },
  });

  return (
    <Combobox
      label="Solicitante (opcional)"
      value={value}
      onChange={onChange}
      onSearch={setTerm}
      options={(search.data ?? []).map((p) => ({
        value: p.id,
        label: p.nome,
        detail: p.email,
      }))}
      loading={ready && (search.isFetching || term !== debounced)}
      minLength={TERM_MIN}
      placeholder="Eu mesmo"
      searchPlaceholder="Nome ou e-mail"
      emptyMessage="Ninguém desta base com esse nome."
      notice={search.isError ? search.error.message : undefined}
      help="Quem pediu este ticket, se não foi você. A pessoa recebe os avisos por e-mail."
    />
  );
}
